import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Stack,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  InputAdornment,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import supportService from '../services/SupportService';
import JobSeekerProfileService from '../services/JobSeekerProfileService';
import CompanyProfileService from '../services/CompanyProfileService';
import SupportCard from '../components/SupportCard';

const SupportPage = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commentMap, setCommentMap] = useState({});
  const [profile, setProfile] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [expandedId, setExpandedId] = useState(null);

  const loadProfile = async () => {
    // Try job seeker first
    const js = await new JobSeekerProfileService().getRecordsByUserId();
    if (js) return { id: js.id, type: 'jobseeker', name: `${js.basicProfile?.firstName || ''} ${js.basicProfile?.lastName || ''}`.trim() };
    const cp = await new CompanyProfileService().getRecordsByUserId();
    if (cp) return { id: cp.id, type: 'company', name: cp.name || cp.companyName || '' };
    return null;
  };

  const fetchCases = async () => {
    try {
      setLoading(true);
      const data = await supportService.getMyCases();
      // sort by updatedAt desc if available
      const list = (data || []).slice().sort((a, b) => {
        const ta = a.updatedAt || a.createdAt || ''
        const tb = b.updatedAt || b.createdAt || ''
        return tb.localeCompare(ta)
      })
      setCases(list);
    } catch (err) {
      console.error('Failed to load cases', err);
      setError('Failed to load your support cases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const p = await loadProfile();
      setProfile(p);
      await fetchCases();
    })();
  }, []);

  const handleAddComment = async (caseId) => {
    const text = commentMap[caseId];
    if (!text) return;
    try {
      await supportService.addComment(caseId, text);
      setSnackbar({ open: true, message: 'Message sent', severity: 'success' });
      setCommentMap((m) => ({ ...m, [caseId]: '' }));
      fetchCases();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to send message', severity: 'error' });
    }
  };

  const handleUpdateStatus = async (caseItem, status) => {
    try {
      await supportService.updateStatus(caseItem.id, status);
      setSnackbar({ open: true, message: 'Status updated', severity: 'success' });
      fetchCases();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  };

  function parseComment(raw) {
    if (!raw || typeof raw !== 'string') return { iso: null, userId: 'Admin', message: String(raw) }
    const parts = raw.split('|')
    if (parts.length < 3) return { iso: null, userId: 'User', message: raw }
    return { iso: parts[0], userId: parts[1], message: parts.slice(2).join('|') }
  }

  const visibleCases = cases.filter((c) => {
    if (filterStatus !== 'all' && c.status !== filterStatus) return false
    if (!search) return true
    const q = search.toLowerCase()
    return (c.title || '').toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q)
  })

  if (loading) return (
    <Container sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Container>
  );

  return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Stack spacing={4}>
          <Box>
            <Typography variant="h4" fontWeight={800} gutterBottom>Hub Support</Typography>
            <Typography variant="body1" color="text.secondary">Need help? Open a ticket below and our team will get back to you shortly.</Typography>
          </Box>

          <SupportCard linkedProfileId={profile?.id} linkedProfileType={profile?.type} linkedProfileName={profile?.name} onCreated={(created) => { fetchCases(); setExpandedId(created.id); }} />

          <Divider />

          <Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>Your Conversations</Typography>
              <TextField select size="small" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} sx={{ width: 150 }}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </TextField>
              <TextField size="small" placeholder="Search conversations..." value={search} onChange={(e) => setSearch(e.target.value)} sx={{ ml: 'auto', width: { xs: '100%', sm: 250 } }} />
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {visibleCases.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed' }}>
                <Typography color="text.secondary">No active cases found. If you need help, use the form above.</Typography>
              </Paper>
            ) : (
              <Stack spacing={2}>
                {visibleCases.map((item) => {
                  const isExpanded = expandedId === item.id;
                  return (
                    <Accordion 
                      key={item.id} 
                      expanded={isExpanded} 
                      onChange={() => setExpandedId(isExpanded ? null : item.id)}
                      sx={{ borderRadius: 2, '&:before': { display: 'none' }, boxShadow: 1, border: '1px solid', borderColor: 'divider' }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%', mr: 2 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={700}>{item.title}</Typography>
                            <Typography variant="caption" color="text.secondary">{new Date(item.createdAt).toLocaleDateString()} · Ticket #{item.id.slice(0, 8)}</Typography>
                          </Box>
                          <Chip 
                            label={item.status || 'open'} 
                            variant="outlined" 
                            size="small"
                            color={item.status === 'open' ? 'error' : 'success'} 
                          />
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ bgcolor: 'action.hover', py: 3 }}>
                        <Box sx={{ mb: 4, px: 1 }}>
                          <Typography variant="overline" color="text.secondary" fontWeight={700}>Initialization</Typography>
                          <Paper sx={{ p: 2, mt: 1, borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
                            <Typography variant="body2">{item.description}</Typography>
                          </Paper>
                        </Box>

                        <Divider sx={{ mb: 3 }}>
                          <Chip label="Messages" size="small" />
                        </Divider>

                        <Stack spacing={2} sx={{ mb: 3 }}>
                          {(item.comments || []).map((c, i) => {
                            const parsed = parseComment(c);
                            const isAdmin = parsed.userId === 'Admin';
                            return (
                              <Box key={i} sx={{ alignSelf: isAdmin ? 'flex-start' : 'flex-end', maxWidth: '85%' }}>
                                <Paper sx={{ 
                                  p: 2, 
                                  bgcolor: isAdmin ? 'background.paper' : 'primary.main',
                                  color: isAdmin ? 'text.primary' : 'primary.contrastText',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 2
                                }}>
                                  <Typography variant="body2">{parsed.message}</Typography>
                                </Paper>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: isAdmin ? 'left' : 'right' }}>
                                  {isAdmin ? 'Labourando Team' : 'You'} · {parsed.iso ? new Date(parsed.iso).toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' }) : ''}
                                </Typography>
                              </Box>
                            );
                          })}
                          {(!item.comments || item.comments.length === 0) && (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ fontStyle: 'italic' }}>Pending review by our team.</Typography>
                          )}
                        </Stack>

                        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <TextField 
                              fullWidth 
                              multiline 
                              rows={2}
                              size="small" 
                              placeholder="Write a message..." 
                              value={commentMap[item.id] || ''} 
                              onChange={(e) => setCommentMap((m) => ({ ...m, [item.id]: e.target.value }))} 
                            />
                            <Stack spacing={1}>
                              <Button variant="contained" onClick={() => handleAddComment(item.id)} disabled={!commentMap[item.id]}>Reply</Button>
                              <Button variant="text" size="small" color="inherit" onClick={() => handleUpdateStatus(item, item.status === 'open' ? 'resolved' : 'open')}>
                                {item.status === 'open' ? 'Resolve' : 'Reopen'}
                              </Button>
                            </Stack>
                          </Stack>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            )}
          </Box>

          <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
            <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
          </Snackbar>
        </Stack>
      </Container>
  );
};

export default SupportPage;
